// bi-events adapter for the experience app — the only entry point for analytics.
// Segment itself lives inside @venncity/bi-events; this file only wires app
// config (identity, release tag, Sentry) and the hub-specific helpers.
import { useMemo } from 'react';
import { useLocation, useRouteLoaderData } from 'react-router';
import * as Sentry from '@sentry/react';
import { startCase } from 'lodash-es';
import { BiAction, BiApp, BiEvent, EVENTS, type BiEventRef, type Identity } from '@venncity/bi-events';
import { createWebBiAnalytics } from '@venncity/bi-events/web';
import type { LoginMethod } from './session.server';
import { queryClient } from '~/lib/cache.client';
import type { SignedInSession } from '~/lib/session/load-session.server';
import { sessionQuery } from '~/lib/session/session-query';

const APP_VERSION = import.meta.env.VITE_RELEASE_TAG || 'unknown';

let knownIdentity: Pick<Identity, 'userId' | 'organizationId'> = {};

// Read when an event is sent, not when a component renders. The session reaches the query cache while the Hub layout
// hydrates, after the root has rendered, and a root that re-rendered for it mid-hydration made React drop the server
// HTML and render the page again. Outside the Hub layout (e.g. the login screen) no session is loaded, so the identity
// stays as last known. Events are only sent in the browser, where the cache exists.
function currentIdentity() {
  const session = queryClient.getQueryData<SignedInSession>(sessionQuery.queryKey);
  if (session) {
    knownIdentity = { userId: session.userId, organizationId: session.organizationId };
  }
  return knownIdentity;
}

const biAnalytics = createWebBiAnalytics({
  app: BiApp.EXPERIENCE_HUB,
  appVersion: APP_VERSION,
  getIdentity: currentIdentity,
  onError: (message, detail) => {
    try {
      if (detail instanceof Error) {
        Sentry.captureException(detail, { extra: { biEvents: message } });
      } else {
        Sentry.captureMessage(`[bi-events] ${message}`, { level: 'error', extra: { detail } });
      }
    } catch {
      // reporting must never break the pipeline
    }
  },
});

export function trackBiEvent<E extends BiEventRef>(event: E, traits: Record<string, unknown> = {}): void {
  biAnalytics.track(event, traits);
}

export function loadAnalytics(writeKey: string): void {
  biAnalytics.load(writeKey);
}

export function identifyUser(userId: string | undefined, traits?: Record<string, unknown>): void {
  biAnalytics.identify(userId, traits);
}

export function trackPage(category: string, name: string): void {
  biAnalytics.page(name, undefined, category);
}

export function useBiEvents() {
  const location = useLocation();
  const rootData = useRouteLoaderData('root') as { SESSION?: { LOGIN_METHOD?: LoginMethod } } | undefined;
  const isImpersonation = rootData?.SESSION?.LOGIN_METHOD === 'IMPERSONATION';

  return useMemo(() => {
    function track<E extends BiEventRef>(event: E, traits: Record<string, unknown> = {}) {
      biAnalytics.track(event, traits);
    }

    function actionClicked(
      event: BiEventRef,
      {
        target,
        element = '',
        properties = {},
        ...props
      }: {
        target?: HTMLButtonElement | HTMLAnchorElement | EventTarget | null;
        element?: string | null;
        properties?: Record<string, string | number | boolean | null | undefined>;
      },
    ) {
      if (isImpersonation) {
        return;
      }
      const payload = {
        text: '',
        location,
        destination: '',
        element,
        ...properties,
        ...props,
      };

      if (target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement) {
        payload.text = target.textContent || '';
      }

      if (target instanceof HTMLAnchorElement) {
        payload.destination = target.href;
      }

      biAnalytics.track(event, payload);
    }

    function handleOnCloseEvent() {
      if (isImpersonation) {
        return;
      }
      actionClicked(BiEvent.GENERAL_APP_CLOSED, {
        target: null,
        element: 'Closed App',
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- remove this and fix code
    function onFormSubmit(event: any, pageName: any) {
      if (isImpersonation) {
        return;
      }
      const formData = new FormData(event.target as HTMLFormElement);

      const fields: Record<string, string | string[]> = {};

      formData.forEach((value, key) => {
        if (key.includes('[')) return;

        if (!Reflect.has(fields, key)) {
          fields[key] = value.toString();
          return;
        }

        if (!Array.isArray(fields[key])) {
          fields[key] = [fields[key].toString()];
        }

        if (Array.isArray(fields[key])) {
          (fields[key] as string[]).push(value.toString());
        }
      });

      fields[event.nativeEvent.submitter.name] = event.nativeEvent.submitter.value;

      biAnalytics.track(BiEvent.GENERAL_HUB_FORM_SUBMITTED, {
        element: pageName,
        ...fields,
      });
    }

    const pathNameArray = location.pathname.split('/');
    const pageName = !isImpersonation ? startCase(pathNameArray.pop()) : '';
    let pageCategory = startCase(pathNameArray[1]);
    pageCategory = pageCategory === '' ? 'App' : pageCategory;

    return {
      track,
      actionClicked,
      handleOnCloseEvent,
      onFormSubmit,
      pageName,
      pageCategory,
    };
  }, [isImpersonation, location]);
}

export { BiAction, BiEvent, EVENTS };
export type { BiEventRef };
